import { lazy, Suspense } from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { Layout as DashboardLayout } from '@/layouts/v2/layout';
import type { RouteObject } from 'react-router';
import { Outlet } from 'react-router-dom';

import { config } from '@/config';
import { Page as NotFoundPage } from '@/pages/not-found';
import { RoleGuard } from '@/components/auth/guards/role-guard';

import SettingsPage from '../pages/settings';
import { ForumsPage } from '../pages/v2/forums';
import { paths } from '../paths';
import { route as accountSettingsRoute } from './account-settings/index';
import { route as announcementsRoute } from './announcements/index';
import { route as authRoute } from './auth/index';
import { route as calendarRoute } from './calendar/index';
import { route as communitySettingsRoute } from './community-settings/index';
import { route as documentsRoute } from './documents/index';
import { route as invitationRoute } from './invitation/index';
import { route as meetingsRoute } from './meetings/index';
import { route as requestsRoute } from './requests/index';
import { v2RequestsRoute } from './requests/v2';
import { route as reservationsRoute } from './reservations/index';
import { route as residentsRoute } from './residents/index';
import { route as settingsRoute } from './settings/index';
import { route as setupRoute } from './setup/index';
import { route as unitsRoute } from './units/index';

const HomePage = lazy(() => import('@/pages/dashboard'));
const DiscussionPage = lazy(() => import('@/pages/v2/discussion'));
const CommunitiesPage = lazy(() => import('@/pages/admin/communities'));
const CommunityPage = lazy(() => import('@/pages/admin/communities/community'));

export const routes: RouteObject[] = [
  authRoute,
  calendarRoute,
  documentsRoute,
  requestsRoute,
  ...(config.features.enableV2Requests ? [v2RequestsRoute] : []),
  meetingsRoute,
  announcementsRoute,
  reservationsRoute,
  unitsRoute,
  residentsRoute,
  setupRoute,
  settingsRoute,
  communitySettingsRoute,
  accountSettingsRoute,
  invitationRoute,
  {
    element: (
      <DashboardLayout>
        <Suspense>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'discussion',
        element: <DiscussionPage />,
      },
      {
        path: 'communities',

        element: (
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
              ACCOUNT_ROLES.ACCOUNT_OWNER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <CommunitiesPage />
          </RoleGuard>
        ),
      },
      {
        path: 'communities/:communityId',
        element: (
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
              ACCOUNT_ROLES.ACCOUNT_OWNER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <CommunityPage />
          </RoleGuard>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];
